// 微電網欄位已串接廠商 API。設為 true 可切回 mock 資料
// （設備斷線期間若需展示完整畫面時使用）。
export const USE_MICROGRID_MOCK = false

// 微電網四張卡片，供各站點的排除名單共用。
// 設備裝在中大白色能源屋，故其餘站點一律排除。
const MICROGRID_CARDS = ['energy_flow', 'today_energy', 'battery', 'carbon']

// 只有氣象感測器的站點，排除名單完全相同，抽出來免得兩邊改到不一致。
const WEATHER_ONLY_EXCLUDED = [
    'gps',
    'water',
    'air_gas',
    'air_particle',
    'tilt',
    'meteor',
    ...MICROGRID_CARDS,
]

// 只有氣象感測器的站點，剩下的六張卡（日照、氣壓、風、雨量、濕度、溫度）
// 剛好就是「氣象監測」的全部內容，「全部總覽」點進去一模一樣；而「地質防災」
// 三張卡只剩雨量一張。兩個頁籤都沒有存在的必要，進站直接看氣象監測。
const WEATHER_ONLY_THEMES = { excludedThemes: ['all', 'disaster'], defaultTheme: 'energy' }

export const StationList = [
    { id: 'ys', excludedData: [...MICROGRID_CARDS] },
    {
        id: 'lf',
        excludedData: [...WEATHER_ONLY_EXCLUDED],
        ...WEATHER_ONLY_THEMES,
    },
    {
        // 巴陵行政中心：資料由現地機器主動推回資料庫（後端 /push_weather/），
        // 不是我們去抓的。站點代碼 newbl 是後端定的，沿用即可。
        id: 'newbl',
        excludedData: [...WEATHER_ONLY_EXCLUDED],
        ...WEATHER_ONLY_THEMES,
    },
    // sg
    // { id: 'sg', excludedData: [] },
    // {
    //     id: 'bl',
    //     excludedData: ['gps', 'water', 'air_gas', 'air_particle'],
    // },
    // 新增三光國小，只保留 tilt (排除其他所有項目)
    {
        id: 'sg',
        excludedData: [
            'solar',
            'wind',
            'rain',
            'pressure',
            'temp',
            'humid',
            'gps',
            'water',
            'air_gas',
            'air_particle',
            'meteor', // 流星影像來自義盛的 HDE/HDN 相機
            ...MICROGRID_CARDS,
        ],
    },
    {
        // 中大白色能源屋：微電網設備的實際所在地。
        id: 'wh',
        // 現地 API 傳回的太陽能與電池數值是壞的，改用廠商匯出的 CSV，
        // 由 backend/import_microgrid_csv.py 灌進 wh 這個 collection。
        //
        // 這批資料只到 2026-07-21 00:00，而最後一筆是半夜、太陽能為 0，
        // 進站就落在那裡等於看到一片空的。改成指定停在 7/20 16:00：
        // 太陽能還有 5,770 W、市電 0 W、自給率 78%，一個畫面看得完整套運作。
        // 廠商恢復連續供料後把這一行刪掉，就會自動回到「停在最新一筆」。
        previewMoment: '2026-07-20T16:00',
        // 能源屋只有微電網一個主題，「全部總覽」與微電網頁籤內容完全重複，
        // 故移除總覽，進站直接看微電網。
        // 氣象監測頁也一併移除：該站的氣象卡幾乎都排除了，頁籤點進去只剩日照
        // 一張卡；而日照放在這站的用途是解釋發電量，微電網頁已經有了。
        excludedThemes: ['all', 'energy'],
        defaultTheme: 'microgrid',
        // 能源屋沒有自己的氣象感測器。日照原本保留，是因為當時整站的資料
        // 借用義盛的紀錄（dataLocation: 'ys'），那張卡讀到的是義盛的輻射量。
        // 改讀 wh 自己的資料之後現地沒有感測器，只會畫出一條 0，故一併排除。
        excludedData: [
            'solar',
            'wind',
            'rain',
            'pressure',
            'temp',
            'humid',
            'gps',
            'water',
            'air_gas',
            'air_particle',
            'tilt',
            'meteor',
        ],
    },
]

/**
 * 取得站點實際的資料來源代碼。
 *
 * 站點代碼與後端的 location 參數通常相同，但新站點在後端建立之前，
 * 可用 dataLocation 暫時指向既有的 location。
 *
 * @param {string} stationId 站點代碼
 * @returns {string} 送給後端的 location 值
 */
export function resolveDataLocation(stationId) {
    const station = StationList.find((s) => s.id === stationId)
    return station?.dataLocation ?? stationId
}

/**
 * 取得站點進站時要停在哪一刻。
 *
 * 一般站點停在最新一筆，但只有匯入資料、沒有即時來源的站點需要指定，
 * 否則會停在資料尾端那個沒有代表性的時間點。
 *
 * @param {string} stationId 站點代碼
 * @returns {string|null} 'YYYY-MM-DDTHH:mm'，沒有指定則為 null
 */
export function resolvePreviewMoment(stationId) {
    const station = StationList.find((s) => s.id === stationId)
    return station?.previewMoment ?? null
}

/**
 * 取得站點的排除主題清單。
 *
 * @param {string} stationId 站點代碼
 * @returns {string[]} 該站不顯示的主題頁籤 id
 */
export function resolveExcludedThemes(stationId) {
    const station = StationList.find((s) => s.id === stationId)
    return station?.excludedThemes ?? []
}

/**
 * 取得切到某站點時要停在哪個主題。
 *
 * @param {string} stationId 站點代碼
 * @returns {string} 主題 id，未指定者為全部總覽
 */
export function resolveDefaultTheme(stationId) {
    const station = StationList.find((s) => s.id === stationId)
    return station?.defaultTheme ?? 'all'
}
